import React, { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, TrendingUp, DollarSign, CheckCircle, Clock } from "lucide-react";
import { useMentorSoldSessions } from "@/hooks/useMentorSoldSessions";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { ptBR } from 'date-fns/locale';
import { useToast } from "@/components/ui/use-toast";
import { MentorSoldSessionCard } from "@/components/mentor/MentorSoldSessionCard";
import { useAuth } from "@/hooks/auth";
import { debugSecurityCheck } from "@/services/mentorSessionsService";

const SessoesVendidas = () => {
  const { sessions, stats, loading, refetch } = useMentorSoldSessions();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [filter, setFilter] = useState<"agendadas" | "concluidas">("agendadas");

  const formatDate = (dateStr: string) => {
    try {
      const date = parseISO(dateStr + 'T00:00:00');
      return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
    } catch (e) {
      console.error("Error formatting date:", e);
      return dateStr;
    }
  };

  const formatCurrency = (cents: number) => {
    return (cents / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  // OTIMIZAÇÃO: Memoizar filtro para evitar re-renderizações desnecessárias
  const filteredSessions = useMemo(() => {
    return sessions.filter(session => {
      if (filter === "agendadas") {
        return session.status === "confirmado" || session.status === "pendente";
      } else {
        return session.status === "concluido";
      }
    });
  }, [sessions, filter]);

  // OTIMIZAÇÃO: Reduzir logs - apenas em desenvolvimento
  React.useEffect(() => {
    if (import.meta.env.DEV && filteredSessions.length > 0) {
      console.log(`📊 [SessoesVendidas] Filter: ${filter}, Sessions: ${filteredSessions.length}/${sessions.length}`);
    }
  }, [filter, filteredSessions.length, sessions.length]);

  // 🔒 Função de teste de segurança disponível no console
  React.useEffect(() => {
    // Disponibilizar função de teste global
    (window as any).testMentorSecurity = async () => {
      if (!user) {
        console.log('❌ Usuário não logado');
        return;
      }
      
      console.log('🔒 Iniciando teste de segurança para mentor:', user.email);
      const result = await debugSecurityCheck(user.id);
      console.log('🔒 Resultado do teste:', result);
      return result;
    };
    
    // Cleanup
    return () => {
      delete (window as any).testMentorSecurity;
    };
  }, [user]);

  const handleRefresh = () => {
    console.log("🔄 [SessoesVendidas] Refreshing data manually...");
    refetch(true); // Force refresh
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 text-[#00B6F0] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h1 className="text-2xl md:text-3xl font-bold text-white">Mentorias Vendidas</h1>
        <Button 
          variant="outline" 
          size="icon"
          onClick={handleRefresh}
          className="h-9 w-9 self-start sm:self-auto"
          title="Atualizar mentorias"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Indicadores de Performance */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total de Mentorias Vendidas (Pagas) */}
        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl md:text-3xl font-bold text-green-400 mb-1">
                  {stats.paidSessions}
                </div>
                <div className="text-xs md:text-sm text-gray-400">
                  Mentorias Vendidas
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  (Com pagamento confirmado)
                </div>
              </div>
              <div className="p-2 bg-green-500/10 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Valor Total Líquido */}
        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl md:text-3xl font-bold text-blue-400 mb-1">
                  {formatCurrency(stats.totalNetValue)}
                </div>
                <div className="text-xs md:text-sm text-gray-400">
                  Valor Líquido
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  (Após comissão)
                </div>
              </div>
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <DollarSign className="h-5 w-5 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Total de Mentorias Concluídas */}
        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl md:text-3xl font-bold text-emerald-400 mb-1">
                  {stats.completedSessions}
                </div>
                <div className="text-xs md:text-sm text-gray-400">
                  Mentorias Concluídas
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  (Status finalizado)
                </div>
              </div>
              <div className="p-2 bg-emerald-500/10 rounded-lg">
                <CheckCircle className="h-5 w-5 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Total de Mentorias Pendentes */}
        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl md:text-3xl font-bold text-yellow-400 mb-1">
                  {stats.scheduledSessions}
                </div>
                <div className="text-xs md:text-sm text-gray-400">
                  Mentorias Agendadas
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  (Confirmadas/Pendentes)
                </div>
              </div>
              <div className="p-2 bg-yellow-500/10 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Card className="bg-[#1E293B] border-[#2A3749]">
        <CardHeader className="p-4 md:p-6">
          <div className="flex flex-col gap-4">
            <div>
              <CardTitle className="text-white text-lg md:text-xl">Histórico de Mentorias</CardTitle>
              <CardDescription className="text-gray-400 text-sm">Mentorias vendidas por você</CardDescription>
            </div>
            <div className="flex gap-2 w-full">
              <Button 
                variant={filter === "agendadas" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("agendadas")}
                className={`flex-1 text-xs md:text-sm ${filter === "agendadas" 
                  ? "bg-[#00B6F0] text-black hover:bg-[#00A3D9]" 
                  : "border-[#2A3749] text-white hover:bg-[#2A3749]"
                }`}
              >
                Agendadas ({stats.scheduledSessions})
              </Button>
              <Button 
                variant={filter === "concluidas" ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter("concluidas")}
                className={`flex-1 text-xs md:text-sm ${filter === "concluidas" 
                  ? "bg-[#00B6F0] text-black hover:bg-[#00A3D9]" 
                  : "border-[#2A3749] text-white hover:bg-[#2A3749]"
                }`}
              >
                Concluídas ({stats.completedSessions})
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          {filteredSessions.length > 0 ? (
            <div className="space-y-4">
              {filteredSessions.map(sessao => (
                <MentorSoldSessionCard
                  key={sessao.id}
                  session={sessao}
                  onActionCompleted={() => refetch(true)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 md:py-12">
              <p className="text-gray-400 text-sm md:text-base">
                {filter === "agendadas" 
                  ? "Você não possui mentorias agendadas no momento" 
                  : "Você ainda não possui mentorias concluídas"
                }
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SessoesVendidas;
